import React, { useState } from 'react';
import { Loader2, LogOut, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';

interface LogoutConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const LogoutConfirmModal: React.FC<LogoutConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  const { isDark } = useTheme();
  const { signOut } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  if (!isOpen) return null;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setLogoutError('');

    try {
      await signOut();
    } catch {
      setLogoutError('Não foi possível encerrar a sessão. Tente novamente.');
      setIsLoggingOut(false);
      return;
    }

    const notificationKeysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (
        key &&
        (
          key === 'taskNotifications' ||
          key === 'taskUnreadCount' ||
          key.startsWith('taskNotifications:') ||
          key.startsWith('taskUnreadCount:')
        )
      ) {
        notificationKeysToRemove.push(key);
      }
    }

    notificationKeysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    onConfirm();
    window.location.href = '/';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl w-full max-w-md shadow-2xl border ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'
      }`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${
          isDark ? 'border-gray-700' : 'border-slate-200'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${
              isDark ? 'bg-red-900/20' : 'bg-red-50'
            }`}>
              <LogOut className={`h-5 w-5 ${
                isDark ? 'text-red-400' : 'text-red-600'
              }`} />
            </div>
            <h2 className={`text-lg font-semibold ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}>Confirmar Saída</h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-xl transition-colors ${
              isDark 
                ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <div className={`border rounded-2xl p-4 mb-6 ${
            isDark 
              ? 'bg-yellow-900/10 border-yellow-700/30' 
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <p className={`text-sm ${
              isDark ? 'text-yellow-300' : 'text-yellow-800'
            }`}>
              Tem certeza que deseja sair do sistema? Você precisará fazer login novamente para acessar sua conta.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoggingOut}
              className={`flex-1 px-4 py-2 rounded-xl border transition-colors ${
                isDark 
                  ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' 
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancelar
            </button>
            <button
              onClick={() => void handleLogout()}
              disabled={isLoggingOut}
              className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            >
              {isLoggingOut ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
              {isLoggingOut ? 'Saindo...' : 'Sair'}
            </button>
          </div>
          {logoutError && (
            <p className="mt-3 text-sm text-red-500" role="alert">
              {logoutError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoutConfirmModal;
